import { createStore, StoreCore } from "@priolo/jon";
import cyclesApi from "api/cycles";
import tasksApi from "api/tasks";
import { TAGS } from "components/planner/types";
import i18n from "i18next";
import { SESSION_TYPE } from "stores/draft/type";
import farmSo from "stores/farm";
import growUnitSo from "stores/growUnit";
import dialogSo, { DIALOG_TYPE } from "stores/layout/dialogStore";
import { CropLot } from "types/CropLot";
import { Cycle } from "types/Cycle";
import { EditCycle } from "./utils/edit";
import { clone } from "utils/func";
import cycleSo from ".";
import draftSo from "stores/draft";



// used when dialog closed
let resolveClose = null

const setup = {

	state: {
		/** indica se la dialog è aperta o no */
		isOpen: false,
		/** indice del tab selezionato */
		tab: <CYCLE_DIALOG_TAB>0,
		/** il CYCLE che si sta modificando */
		cycle: <Cycle>null,
		/** indica che il bottone SYNC è disabilitato */
		syncDisabled: false,
		/** CROP-LOT */
		editCropLotOpen: false,
		editCropLot: <CropLot>null,
		/** tutti i LAYERS selezionati */
		layersNumberSelected: <number[]>[],
	},

	getters: {
	},

	actions: {
		/** apre la dialog con il cycle da mostrare */
		open: async (
			{ cycle, tab }: { cycle: Cycle, tab?: CYCLE_DIALOG_TAB },
			store?: CycleDialogStore
		) => {
			if (!cycle /*|| cycle._edit?.type == EDIT_TYPE.DELETED || !!cycle.rejected*/) return
			if (tab == null) tab = CYCLE_DIALOG_TAB.DETAIL
			const farmId = farmSo.state.select.id
			store.setTab(tab)
			store.setLayersNumberSelected([])
			store.setSyncDisabled(false)

			// clone da editare
			let cycleDetail = EditCycle.current(cycle, true, true)
			// se il CYCLE NON stato modificato allora carico il dettaglio principalmente per avere il consumo
			if (!cycle._edit && cycle.id && EditCycle.getExtra(cycle, TAGS.SESSION) != SESSION_TYPE.PREVIEW) {
				const { data: cycleLoaded } = await cyclesApi.get(farmId, cycle.id)
				cycleDetail = { ...cycleDetail, ...cycleLoaded }
			}
			// lo setto da editare
			store.setCycle(cycleDetail)
			// apro la dialog
			store.setIsOpen(true)
			return new Promise((resolve, reject) => resolveClose = resolve)
		},
		/** chiude la dialog  */
		close: async (type: CYCLE_EDIT_TYPE, store?: CycleDialogStore) => {
			if (resolveClose) resolveClose(type)
			resolveClose = null
			store.setIsOpen(false)
		},
		editCropLot: (cropLot: CropLot, store?: CycleDialogStore) => {
			if (!cropLot) return
			store.setEditCropLot(clone(cropLot))
			store.setEditCropLotOpen(true)
		},
		saveCropLot: async (_: void, store?: CycleDialogStore) => {
			store.setEditCropLotOpen(false)
			const lotCode = store.state.editCropLot.lotCode
			const seedUsed = store.state.editCropLot.seedUsedG
			const gelUsed = store.state.editCropLot.gelUsedG

			await Promise.all([
				cyclesApi.editCropLotSeedUsed(lotCode, seedUsed),
				cyclesApi.editCropLotGelUsed(lotCode, gelUsed),
			])

			const cropLotOrig = cycleSo.getByLotCode(lotCode)?.cropLots
				?.find(cropLot => cropLot.lotCode == lotCode)
			cropLotOrig.seedUsedG = seedUsed
			cropLotOrig.gelUsedG = gelUsed
			cycleSo._update()

			dialogSo.dialogOpen({ type: DIALOG_TYPE.INFO, modal: false, text: "CROP LOT EDITED SUCCESSFULLY" })
		},
		/** Permette di settare lo YIELD per questo CYCLE */
		setYieldCropLot: (crop: Partial<CropLot>, store?: CycleDialogStore) => {
			const cropLot = store.state.cycle?.cropLots?.find(cropLot => cropLot.id == crop.id)
			if (!cropLot) return
			if (crop.yield != null) cropLot.yield = crop.yield
			if (crop.unitsYield != null) cropLot.unitsYield = crop.unitsYield
			store.setCycle({ ...store.state.cycle })
		},
		/** -> BE tutti i crop_lots del CYCLE */
		harvestCropLots: async (_: void, store?: CycleDialogStore) => {
			const promises = store.state.cycle?.cropLots
				?.filter(cropLot => cropLot.yield != null || cropLot.unitsYield != null)
				?.map(cropLot => cyclesApi.harvestCropLot(cropLot.lotCode, cropLot.yield, cropLot.unitsYield));
			await Promise.all(promises)

			// feedback and close
			dialogSo.dialogOpen({ type: DIALOG_TYPE.INFO, modal: false, text: i18n.t("snackbar.cycle.harvested") })
			store.close(CYCLE_EDIT_TYPE.HARVEST)
		},
		split: async (_: void, store?: CycleDialogStore) => {
			await draftSo.split({
				cycle: store.state.cycle,
				layersNumber: store.state.layersNumberSelected
			})
			store.setLayersNumberSelected([])
			dialogSo.dialogOpen({
				type: DIALOG_TYPE.INFO,
				modal: false,
				text: i18n.t("snackbar.cycle.split")
			})
		},
		/** sincronizza tutti i TASKS di un CYCLE */
		taskSync: async (_: void, store?: CycleDialogStore) => {
			const cycleUuid = store.state.cycle?.cycleUuid
			const growUnitId = store.state.cycle?.growUnitId
			const growUnit = growUnitSo.getByID(growUnitId)
			if (!growUnit || !cycleUuid) return
			await tasksApi.sync(growUnit.farmId, cycleUuid)
			store.setSyncDisabled(true)
			dialogSo.dialogOpen({ type: DIALOG_TYPE.INFO, modal: false, text: i18n.t("snackbar.task.sync") })
		},

	},

	mutators: {
		setIsOpen: (isOpen: boolean) => ({ isOpen }),
		setTab: (tab: number) => ({ tab }),
		setCycle: (cycle: Cycle) => ({ cycle }),
		setSyncDisabled: (syncDisabled: boolean) => ({ syncDisabled }),

		setEditCropLotOpen: (editCropLotOpen: boolean) => ({ editCropLotOpen }),
		setEditCropLot: (editCropLot: CropLot) => ({ editCropLot }),

		setLayersNumberSelected: (layersNumberSelected: number[]) => ({ layersNumberSelected }),
	},
}


export type CycleDialogState = typeof setup.state
export type CycleDialogGetters = typeof setup.getters
export type CycleDialogActions = typeof setup.actions
export type CycleDialogMutators = typeof setup.mutators
export interface CycleDialogStore extends StoreCore<CycleDialogState>, CycleDialogGetters, CycleDialogActions, CycleDialogMutators {
	state: CycleDialogState
}
const cycleDialogSo = createStore(setup) as CycleDialogStore
export default cycleDialogSo

export enum CYCLE_EDIT_TYPE {
	CLOSE,
	REJECT,
	HARVEST,
	ERROR,
}

export enum CYCLE_DIALOG_TAB {
	DETAIL = 0,
	CROP_LOTS = 1,
	TASKS = 2,
	CONSUMPTION = 3,
	LOST_TROLLEY = 4,
	PHOTOS = 5,
}